import React, {useState} from 'react';
import {
    Datagrid,
    Pagination as RaPagination,
    ReferenceManyField,
    Show,
    TextField,
    useGetList,
    useListContext,
    usePermissions,
    useRecordContext,
    useRedirect,
    useRefresh,
    useTranslate,
    FunctionField,
} from 'react-admin';
import {
    Alert,
    AlertTitle,
    Avatar,
    Box,
    FormControl,
    FormHelperText,
    Link as MuiLink,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import {DeleteOutline, EditOutlined, OpenInNew} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SectionCard from '../components/statamic/SectionCard';
import DataRow from '../components/statamic/DataRow';
import StatusDot from '../components/statamic/StatusDot';
import StatusPill from '../components/statamic/StatusPill';
import StatamicTabs, {useTabs} from '../components/statamic/StatamicTabs';
import DeleteConfirmDialog from '../components/statamic/DeleteConfirmDialog';
import useDeleteConfirm from '../components/statamic/useDeleteConfirm';
import {axiosApiInstance} from '../AxiosUtils';
import hasPermission from '../auth/hasPermission';
import TranslationTable from '../components/TranslationTable';
import DynamicMetadataDisplay from '../components/DynamicMetadataDisplay';
import {formatDateTime} from '../lib/dates';

const formatCurrency = (value, currency = 'EUR') => {
    if (value == null) return null;
    try {
        return new Intl.NumberFormat('en', {style: 'currency', currency}).format(value);
    } catch {
        return `${value} ${currency}`;
    }
};

const BooleanCell = ({value, trueLabel = 'Yes', falseLabel = 'No'}) => (
    <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 1}}>
        <StatusDot variant={value ? 'success' : 'neutral'}/>
        <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.primary}}>
            {value ? trueLabel : falseLabel}
        </Typography>
    </Box>
);

const ExternalLink = ({href, children}) => (
    <MuiLink
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: t.color.accent.primary,
            fontSize: `${t.type.body.size}px`,
            textDecoration: 'none',
            wordBreak: 'break-all',
            '&:hover': {textDecoration: 'underline'},
        }}
    >
        {children}
        <OpenInNew sx={{fontSize: 13, flexShrink: 0}}/>
    </MuiLink>
);

const MetaRow = ({label, children}) => (
    <Box sx={{display: 'grid', gridTemplateColumns: '160px 1fr', alignItems: 'baseline', gap: 1.5, py: 0.5}}>
        <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary, fontWeight: 500}}>
            {label}
        </Typography>
        <Box sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.primary, minWidth: 0}}>
            {children || <Box component="span" sx={{color: t.color.text.muted}}>—</Box>}
        </Box>
    </Box>
);

const CharityHero = ({record, logoUrl, translate}) => {
    const locationText = [record.city, record.country].filter(Boolean).join(', ') || null;
    const sponsorshipText = formatCurrency(record.sponsorship_amount, record.default_currency);

    return (
        <Box
            sx={{
                backgroundColor: t.color.surface,
                border: `1px solid ${t.color.border.default}`,
                borderRadius: `${t.radius.md}px`,
                p: 3,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 4,
                alignItems: 'stretch',
            }}
        >
            <Box sx={{display: 'flex', gap: 3, alignItems: 'center', minWidth: 0}}>
                <Avatar
                    src={logoUrl}
                    variant="rounded"
                    sx={{
                        width: 96,
                        height: 96,
                        fontSize: 34,
                        fontWeight: 600,
                        flexShrink: 0,
                        bgcolor: t.color.sidebarHover,
                        color: t.color.text.secondary,
                        border: `1px solid ${t.color.border.hairline}`,
                    }}
                >
                    {record.charity_name?.charAt(0)}
                </Avatar>
                <Box sx={{minWidth: 0, flex: 1}}>
                    <Typography
                        sx={{
                            fontSize: 22,
                            fontWeight: 700,
                            color: t.color.text.primary,
                            lineHeight: 1.2,
                            wordBreak: 'break-word',
                        }}
                    >
                        {record.charity_name || 'Unnamed'}
                    </Typography>
                    {record.project_number && (
                        <Typography sx={{mt: 0.5, fontSize: `${t.type.small.size}px`, color: t.color.text.secondary, fontStyle: 'italic'}}>
                            {translate('Project number')} {record.project_number}
                        </Typography>
                    )}
                    <Box sx={{display: 'flex', gap: 0.75, mt: 1.5, flexWrap: 'wrap'}}>
                        <StatusPill variant={record.active ? 'success' : 'neutral'}>
                            {record.active ? translate('Active') : translate('Inactive')}
                        </StatusPill>
                        {record.demo_charity && <StatusPill variant="info">Demo</StatusPill>}
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    pl: 3,
                    borderLeft: `1px solid ${t.color.border.hairline}`,
                }}
            >
                <MetaRow label={translate('Email')}>
                    {record.email ? (
                        <MuiLink
                            href={`mailto:${record.email}`}
                            sx={{color: t.color.accent.primary, textDecoration: 'none', '&:hover': {textDecoration: 'underline'}}}
                        >
                            {record.email}
                        </MuiLink>
                    ) : null}
                </MetaRow>
                <MetaRow label={translate('Location')}>{locationText}</MetaRow>
                <MetaRow label={translate('Sponsorship amount')}>{sponsorshipText}</MetaRow>
                <MetaRow label={translate('Website link')}>
                    {record.website_link ? (
                        <ExternalLink href={record.website_link}>{record.website_link}</ExternalLink>
                    ) : null}
                </MetaRow>
            </Box>
        </Box>
    );
};

const NewPostMailAction = ({record, permissions, translate}) => {
    const [sending, setSending] = useState(false);
    const [status, setStatus] = useState(null);
    const [mailsSent, setMailsSent] = useState(0);
    const [triedSending, setTriedSending] = useState(0);
    const [nrOfWeeks, setNrOfWeeks] = useState(2);

    const triggerMail = () => {
        if (!record) return;
        setSending(true);
        setStatus(null);
        const rolePath = permissions === 'admin' ? 'admin' : 'umbrellad';
        axiosApiInstance.get(
            `${process.env.REACT_APP_API_URL}/api/v1/${rolePath}/trigger/mail/${record.id}/${nrOfWeeks}`
        ).then((response) => {
            setSending(false);
            if (response.status === 200) {
                setStatus('success');
                if (response.data?.send_mail_info) {
                    setMailsSent(response.data.send_mail_info.mailsSent);
                    setTriedSending(response.data.send_mail_info.triedSending);
                }
            } else {
                setStatus('error');
            }
        }).catch(() => {
            setSending(false);
            setStatus('error');
        });
    };

    return (
        <Box>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'}}>
                <FormControl sx={{minWidth: 80}}>
                    <Select
                        value={nrOfWeeks}
                        onChange={(e) => setNrOfWeeks(e.target.value)}
                        size="small"
                        sx={{fontSize: `${t.type.body.size}px`, backgroundColor: t.color.surface}}
                    >
                        {[1, 2, 3, 4, 5, 6].map((n) => (
                            <MenuItem key={n} value={n}>{n}</MenuItem>
                        ))}
                    </Select>
                    <FormHelperText sx={{fontSize: 11, color: t.color.text.muted}}>
                        {translate('# weeks back')}
                    </FormHelperText>
                </FormControl>
                <LoadingButton
                    onClick={triggerMail}
                    loading={sending}
                    variant="outlined"
                    size="small"
                    sx={{
                        textTransform: 'none',
                        fontSize: `${t.type.small.size}px`,
                        borderColor: t.color.border.default,
                        color: t.color.text.primary,
                        '&:hover': {borderColor: t.color.accent.primary, color: t.color.accent.primary, backgroundColor: 'transparent'},
                    }}
                >
                    {translate('Send new post mail')}
                </LoadingButton>
            </Box>
            {status === 'success' && (
                <Alert severity="success" sx={{mt: 1, fontSize: `${t.type.small.size}px`}}>
                    <AlertTitle sx={{fontSize: `${t.type.body.size}px`}}>Success</AlertTitle>
                    {mailsSent} mail(s) sent. (Tried #{triedSending})
                </Alert>
            )}
            {status === 'error' && (
                <Alert severity="warning" sx={{mt: 1, fontSize: `${t.type.small.size}px`}}>
                    <AlertTitle sx={{fontSize: `${t.type.body.size}px`}}>Warning</AlertTitle>
                    Could not send mails.
                </Alert>
            )}
        </Box>
    );
};

// Resolves the opaque OVAS location_id to a human title client-side (the
// charity record only carries the id, the title lives on the umbrella's
// locations list). Falls back to the raw id if the list hasn't matched it
// (page-100 cap, or the location was removed on the OVAS side), or if the
// fetch was skipped outright / errors — with a muted secondary hint on the
// error case only, so the id is never left unexplained.
const LocationValue = ({record, canSeeLocation, permissions, translate}) => {
    // v2's admin-path locations endpoint REQUIRES umbrella_id (400 without
    // it) — an admin viewing a standalone charity (no parent umbrella) has
    // nothing to scope that fetch by, so don't fire a guaranteed-400 request.
    const fetchEnabled = canSeeLocation && !!record.location_id && (permissions !== 'admin' || !!record.parent_charity_id);
    const {data: locations, error} = useGetList(
        'locations',
        {
            filter: record.parent_charity_id ? {umbrella_id: record.parent_charity_id} : {},
            pagination: {page: 1, perPage: 100},
        },
        {enabled: fetchEnabled}
    );

    if (!record.location_id) return null;

    if (!fetchEnabled) {
        return record.location_id;
    }

    if (error) {
        return (
            <>
                {record.location_id}
                <Typography
                    component="span"
                    sx={{display: 'block', fontSize: `${t.type.caption.size}px`, color: t.color.text.muted, mt: 0.25}}
                >
                    {translate('charity.show_location_unavailable', {_: 'Location list unavailable'})}
                </Typography>
            </>
        );
    }

    const match = (locations || []).find((l) => String(l.id) === String(record.location_id));
    return match ? match.title : record.location_id;
};

const DetailsTab = ({record, logoUrl, permissions, translate}) => {
    const canSeeUmbrella = hasPermission(permissions, ['view'], 'umbrella');
    const canSeeLocation = hasPermission(permissions, ['view'], 'charity.location');
    const createdAt = formatDateTime(record.created_at);
    const updatedAt = formatDateTime(record.updated_at);

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
            <CharityHero record={record} logoUrl={logoUrl} translate={translate}/>

            {record.description && (
                <SectionCard title={translate('Description')}>
                    <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.primary, whiteSpace: 'pre-wrap'}}>
                        {record.description}
                    </Typography>
                </SectionCard>
            )}

            <SectionCard title={translate('General info')}>
                <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', columnGap: 3, rowGap: 0}}>
                    <DataRow label={translate('Short name')} value={record.short_name}/>
                    <DataRow label={translate('Sponsorship amount')} value={formatCurrency(record.sponsorship_amount, record.default_currency)}/>
                    {canSeeUmbrella && <DataRow label={translate('Umbrella')} value={record.parent_charity_name}/>}
                    {canSeeLocation && (
                        <DataRow label={translate('Location')}>
                            {record.location_id ? (
                                <LocationValue record={record} canSeeLocation={canSeeLocation} permissions={permissions} translate={translate}/>
                            ) : null}
                        </DataRow>
                    )}
                    <DataRow label={translate('Created at')} value={createdAt} hideWhenEmpty/>
                    <DataRow label={translate('Updated at')} value={updatedAt} hideWhenEmpty/>
                </Box>
            </SectionCard>

            <SectionCard title={translate('Default project coordinator')}>
                {record.default_project_coordinator_name ? (
                    <Box>
                        <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.primary}}>
                            {record.default_project_coordinator_name}
                        </Typography>
                        <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary, mt: 0.25}}>
                            {translate('charity.coordinator_assigned_to_posts')}
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.secondary}}>
                            {translate('charity.no_default_coordinator')}
                        </Typography>
                        <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.muted, mt: 0.25}}>
                            {translate('charity.first_available_coordinator')}
                        </Typography>
                    </Box>
                )}
            </SectionCard>

            <SectionCard title={translate('Payment')}>
                <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 3, rowGap: 0}}>
                    <DataRow label={translate('Stripe Payment Link URL')}>
                        {record.payment_link_url
                            ? <ExternalLink href={record.payment_link_url}>{record.payment_link_url}</ExternalLink>
                            : null}
                    </DataRow>
                    <DataRow label={translate('PayPal Payment Link URL')}>
                        {record.paypal_payment_link_url
                            ? <ExternalLink href={record.paypal_payment_link_url}>{record.paypal_payment_link_url}</ExternalLink>
                            : null}
                    </DataRow>
                </Box>
            </SectionCard>

            <SectionCard
                title={translate('Mail settings')}
                actions={record.send_new_post_mail && (
                    <NewPostMailAction record={record} permissions={permissions} translate={translate}/>
                )}
            >
                <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 3, rowGap: 0}}>
                    <DataRow label={translate('Email')} value={record.email}/>
                    <DataRow label={translate('Send new post mail?')}>
                        <BooleanCell value={record.send_new_post_mail}/>
                    </DataRow>
                </Box>
            </SectionCard>

            <SectionCard title={translate('Address')}>
                <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', columnGap: 3, rowGap: 0}}>
                    <DataRow label={translate('City')} value={record.city}/>
                    <DataRow label={translate('Country')} value={record.country}/>
                    <DataRow label={translate('Nr of countries')} value={record.nr_of_countries}/>
                    <DataRow label={translate('Nr of projects')} value={record.nr_of_projects}/>
                    <DataRow label="Lat" value={record.lat}/>
                    <DataRow label="Lng" value={record.lng}/>
                </Box>
            </SectionCard>

            <SectionCard title={translate('Message Type Settings')}>
                <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', columnGap: 3, rowGap: 1}}>
                    <BooleanCell value={record.allow_text_messages}  trueLabel={translate('Text messages allowed')}  falseLabel={translate('Text messages disabled')}/>
                    <BooleanCell value={record.allow_photo_messages} trueLabel={translate('Photo messages allowed')} falseLabel={translate('Photo messages disabled')}/>
                    <BooleanCell value={record.allow_video_messages} trueLabel={translate('Video messages allowed')} falseLabel={translate('Video messages disabled')}/>
                </Box>
            </SectionCard>

            <SectionCard title={translate('Alert info')}>
                <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 3, rowGap: 0}}>
                    <DataRow label={translate('Days orange alert')} value={record.days_orange_alert}/>
                    <DataRow label={translate('Days red alert')} value={record.days_red_alert}/>
                </Box>
            </SectionCard>

            <SectionCard title={translate('Custom Fields')}>
                <DynamicMetadataDisplay entityType="CHARITY"/>
            </SectionCard>
        </Box>
    );
};

const TranslationsTab = ({record}) => (
    <SectionCard title="Translations">
        <TranslationTable translations={record.translations}/>
    </SectionCard>
);

const SponsorMailBulkActions = ({record, permissions, translate}) => {
    const {selectedIds} = useListContext();
    const refresh = useRefresh();
    const [sending, setSending] = useState(false);

    const sendMail = () => {
        if (!record || !selectedIds?.length) return;
        const rolePath = permissions === 'admin' ? 'admin' : 'umbrellad';
        const formData = new FormData();
        formData.append('selectedIds', selectedIds);
        setSending(true);
        axiosApiInstance.post(
            `${process.env.REACT_APP_API_URL}/api/v1/${rolePath}/updatePostWaiting/${record.id}`,
            formData,
            {headers: {'Content-Type': 'multipart/form-data'}}
        ).then(() => { setSending(false); refresh(); })
          .catch(() => setSending(false));
    };

    return (
        <LoadingButton
            onClick={sendMail}
            loading={sending}
            variant="outlined"
            size="small"
            sx={{
                textTransform: 'none',
                fontSize: `${t.type.small.size}px`,
                borderColor: t.color.border.default,
                color: t.color.text.primary,
                '&:hover': {borderColor: t.color.accent.primary, color: t.color.accent.primary, backgroundColor: 'transparent'},
            }}
        >
            {translate('Send mail')}
        </LoadingButton>
    );
};

const MailSponsorsTab = ({record, permissions, translate}) => (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
        <SectionCard title={translate('Sponsor list')} subtitle={translate('Sponsor list info')}>
            <ReferenceManyField reference="updateWaitingList" target="charity_id" perPage={25} pagination={<RaPagination/>}>
                <Datagrid bulkActionButtons={<SponsorMailBulkActions record={record} permissions={permissions} translate={translate}/>} optimized>
                    <TextField source="guardian_name" label={translate('Guardian name')} sortable={false}/>
                    <TextField source="child_name" label={translate('Child name')} sortable={false}/>
                    <TextField source="last_access" label={translate('Last access')} sortable={false}/>
                    <TextField source="language" label={translate('Language')} sortable={false}/>
                </Datagrid>
            </ReferenceManyField>
        </SectionCard>
        <SectionCard title={translate('Sponsor list mailed')}>
            <ReferenceManyField
                reference="mailLogs"
                target="charity_id"
                perPage={20}
                pagination={<RaPagination/>}
                sort={{field: 'sent_date_time', order: 'DESC'}}
            >
                <Datagrid bulkActionButtons={false}>
                    <TextField source="sent_date_time" label={translate('Last mail sent')}/>
                    <TextField source="mail_type" label="Mail type"/>
                    <TextField source="recipient" label={translate('Guardian name')}/>
                    <TextField source="last_access_date_time" label={translate('Last access')} sortable={false}/>
                    <TextField source="sender" label={translate('Sender')}/>
                    <TextField source="charity" label={translate('Charity')}/>
                    <TextField source="child" label={translate('Child name')}/>
                    <TextField source="subject" label={translate('Subject')}/>
                    <FunctionField label={translate('Sent successfully')} render={(r) => (r.success ? 'Yes' : 'No')}/>
                </Datagrid>
            </ReferenceManyField>
        </SectionCard>
    </Box>
);

const CharityShowContent = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const [tab, setTab] = useTabs('details');
    const deleteConfirm = useDeleteConfirm();

    if (!record) return null;

    const logoUrl = record.logo_name && record.ext
        ? `${process.env.REACT_APP_CDN_URL}/${record.logo_name}.${record.ext}`
        : undefined;

    const canSeeMailLogs = hasPermission(permissions, ['*'], 'mailLogs');
    const canEdit = hasPermission(permissions, ['edit'], 'charities');
    // v2 flips charity delete to umbrella_admin-only — same flip made on the edit screen.
    const canDelete = permissions === 'umbrella';

    const breadcrumb = record.parent_charity_id && record.parent_charity_name
        ? [
            {label: translate('Umbrellas'), to: '/umbrellas'},
            {label: record.parent_charity_name, to: `/umbrellas/${record.parent_charity_id}/show`},
            {label: record.charity_name || 'Unnamed'},
          ]
        : {label: translate('Charities'), to: '/charities'};

    const statusVariant = record.demo_charity ? 'info' : record.active ? 'success' : 'neutral';

    const tabs = [
        {id: 'details', label: translate('Details')},
        {id: 'translations', label: translate('Translations')},
        ...(canSeeMailLogs ? [{id: 'mail', label: translate('Mail sponsors')}] : []),
    ];

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <PageHeader
                breadcrumb={breadcrumb}
                title={
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <StatusDot variant={statusVariant}/>
                        <Box component="span">{record.charity_name || 'Unnamed'}</Box>
                    </Box>
                }
                actions={
                    <>
                        {canEdit && (
                            <PrimaryButton
                                variant="primary"
                                startIcon={<EditOutlined sx={{fontSize: 16}}/>}
                                onClick={() => redirect('edit', 'charities', record.id)}
                            >
                                {translate('Edit')}
                            </PrimaryButton>
                        )}
                        {canDelete && (
                            <PrimaryButton
                                variant="danger"
                                startIcon={<DeleteOutline sx={{fontSize: 16}}/>}
                                onClick={deleteConfirm.openDialog}
                            >
                                {translate('Delete')}
                            </PrimaryButton>
                        )}
                    </>
                }
            />

            <StatamicTabs tabs={tabs} value={tab} onChange={setTab}/>

            <Box sx={{p: 3}}>
                {tab === 'details' && <DetailsTab record={record} logoUrl={logoUrl} permissions={permissions} translate={translate}/>}
                {tab === 'translations' && <TranslationsTab record={record}/>}
                {tab === 'mail' && canSeeMailLogs && <MailSponsorsTab record={record} permissions={permissions} translate={translate}/>}
            </Box>

            <DeleteConfirmDialog
                open={deleteConfirm.open}
                onClose={deleteConfirm.closeDialog}
                resource="charities"
                record={record}
                recordLabel={record.charity_name}
            />
        </Box>
    );
};

const CharityShowStatamic = (props) => (
    <Show {...props} actions={false} component={Box} sx={{backgroundColor: t.color.page}}>
        <CharityShowContent/>
    </Show>
);

export default CharityShowStatamic;
